import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { pushSimpleEvent } from "@nju/data-layer";
import {
  ChooseNewNumberExpired,
  ChooseNewNumberForm,
  ChooseNewNumberNoResults,
} from "@nju/feature-portal-msisdn";
import { portalRoutes } from "@nju/routes";
import {
  A,
  BlockContent,
  Button,
  CircleLoader,
  GeneralErrorNotification,
  Panel,
  Separator,
  Text,
} from "@nju/ui";
import { mySimCardChangeNumberMachine } from "./mySimCardChangeNumber.machine";
import { MySimCardChangeNumberConfirm } from "./MySimCardChangeNumberConfirm";
import { MySimCardChangeNumberLimitReached } from "./MySImCardChangeNumberLimitReached";
import { MySimCardChangeNumberSuccess } from "./MySimCardChangeNumberSuccess";

export function MySimCardChangeNumberPage() {
  const router = useRouter();
  const params = portalRoutes.mojeKonto.mojaKarta.zmienNumer
    .input(router.query)
    .unwrap();

  const [current, send] = useMachine(mySimCardChangeNumberMachine, {
    actions: {
      onCancelOrderSuccess: () => {
        router.push(
          portalRoutes.mojeKonto.mojaKarta.index.get({
            ...(params?.userId && { userId: params.userId }),
          }),
        );
      },
    },
    context: {
      userId: params?.userId,
    },
  });
  const { matches, context } = current;
  const {
    numbers,
    selectedNumber,
    chooseNewNumber,
    deleteAfterDate,
    errorMessage,
  } = context;

  const handleChangeNumber = React.useCallback(() => {
    send({
      type: "CHANGE_NUMBER",
    });
  }, [send]);

  const handleSetSelectedNumber = React.useCallback(
    (number: string) => {
      send({ type: "SET_SELECTED_NUMBER", value: number });
    },
    [send],
  );

  const handleFetchNewNumbers = React.useCallback(() => {
    send({ type: "FETCH_NEW_NUMBERS" });
  }, [send]);

  const handleCommitOrder = React.useCallback(() => {
    send({
      type: "COMMIT_ORDER",
    });
  }, [send]);

  const handleCancelOrder = React.useCallback(() => {
    send({
      type: "CANCEL_ORDER",
    });
  }, [send]);

  const handlePushDataLayer = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "process_finish_later",
    });
  }, []);

  const handleSubmit = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  if (matches("init")) {
    return (
      <div className="flex justify-center">
        <BlockContent border="none" rounded="primary" spacing="l" tone="none">
          <div className="flex justify-center items-center h-full">
            <CircleLoader />
          </div>
        </BlockContent>
      </div>
    );
  }

  if (matches("changeNumber")) {
    return (
      <Panel testId="choose-number-panel">
        <ChooseNewNumberForm
          errorMessage={errorMessage}
          flow="CHANGE_NUMBER"
          handleFetchNewNumbers={handleFetchNewNumbers}
          handleSetSelectedNumber={handleSetSelectedNumber}
          handleSubmit={handleSubmit}
          isLoading={matches({ changeNumber: "loading" })}
          isLoadingSubmit={matches({ changeNumber: "newOrder" })}
          listOfNumbers={numbers}
          onFinishLater={handlePushDataLayer}
          selectedNumber={selectedNumber}
          userId={params?.userId}
        />
      </Panel>
    );
  }

  if (matches("expiredScreen")) {
    return (
      <Panel>
        <ChooseNewNumberExpired onSubmit={handleFetchNewNumbers} />
      </Panel>
    );
  }

  if (matches("noDataScreen")) {
    return (
      <Panel>
        <ChooseNewNumberNoResults onSubmit={handleFetchNewNumbers} />
      </Panel>
    );
  }

  if (matches("confirmChangeNumber")) {
    return (
      <MySimCardChangeNumberConfirm
        handleCancelOrder={handleCancelOrder}
        handleCommitOrder={handleCommitOrder}
        isLoadingCancel={matches({ confirmChangeNumber: "cancelOrder" })}
        isLoadingCommit={matches({ confirmChangeNumber: "commitOrder" })}
        selectedNumber={chooseNewNumber}
      />
    );
  }

  if (matches("success")) {
    return (
      <MySimCardChangeNumberSuccess
        chooseNewNumber={chooseNewNumber}
        userId={params?.userId}
      />
    );
  }

  if (matches("orderCompleted")) {
    return (
      <MySimCardChangeNumberLimitReached
        anniversaryDate={deleteAfterDate}
        userId={params?.userId}
      />
    );
  }

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return window.location.reload();
        }}
      />
    );
  }

  if (matches("ready")) {
    return (
      <Panel testId="change-number-confirm-info-panel">
        <Text
          size={{
            "@onlyMobile": "xl",
            "@fromTablet": "keySM",
          }}
          weight="bold"
        >
          zmiana numeru
        </Text>
        <Separator size="xxs" tone="invisible" />
        <Text
          size={{
            "@fromTablet": "l",
            "@onlyMobile": "m",
          }}
        >
          Numer możesz zmienić w każdej chwili bez dodatkowych opłat. Zajmie to
          maksymalnie kilka minut.
          <Separator size="s" tone="invisible" />
          Numer możesz zmienić 1 raz w trakcie okresu rozliczeniowego.
        </Text>
        <Separator size="s" tone="invisible" />
        <div className="flex justify-between only-mobile:gap-3 only-mobile:flex-col-reverse flex-row">
          <Link
            href={portalRoutes.mojeKonto.mojaKarta.index.get({
              ...(params?.userId && { userId: params.userId }),
            })}
            passHref={true}
          >
            <A
              size={{
                "@onlyMobile": "s",
              }}
              variant="secondary"
            >
              anuluj
            </A>
          </Link>
          <Button
            isLoading={matches("changeNumber")}
            onPress={handleChangeNumber}
            size={{ "@onlyMobile": "s" }}
          >
            zmień numer
          </Button>
        </div>
      </Panel>
    );
  }

  // eslint-disable-next-line unicorn/no-null
  return null;
}
